// Shared site-wide behaviors used across Revibe sections.
// Section-specific behaviors live inline inside each section file.
// Anything that a single section already owns (scroll progress, stat counters,
// timeline progress, nav scroll state) is deliberately NOT duplicated here.
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var COARSE = window.matchMedia && window.matchMedia('(pointer:coarse)').matches;

  // ===== REVEAL ON SCROLL (used by every section via .reveal class) =====
  // Runs against a scope so sections re-rendered by the theme editor get wired up again.
  function initReveals(scope) {
    var reveals = (scope || document).querySelectorAll('.reveal:not(.v)');
    if (!reveals.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      reveals.forEach(function (r) { r.classList.add('v'); });
      return;
    }

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('v');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach(function (r) { obs.observe(r); });
  }

  // ===== SMOOTH SCROLL FOR IN-PAGE ANCHORS =====
  // Delegated: covers links inside sections added later by the theme editor.
  function initSmoothScroll() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var href = a.getAttribute('href');
      if (!href || href.length < 2) return;
      var t;
      try { t = document.querySelector(href); } catch (err) { return; }
      if (t) {
        e.preventDefault();
        t.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      }
    });
  }

  // ===== MAGNETIC BUTTON EFFECT =====
  // Delegated so [data-magnetic] elements in any section work without re-init.
  function initMagneticButtons() {
    if (REDUCED || COARSE) return;
    document.addEventListener('mousemove', function (e) {
      var btn = e.target.closest && e.target.closest('[data-magnetic]');
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      btn.style.transform =
        'translate(' + (e.clientX - r.left - r.width / 2) * 0.1 + 'px,' +
        (e.clientY - r.top - r.height / 2) * 0.1 + 'px)';
    }, { passive: true });
    document.addEventListener('mouseout', function (e) {
      var btn = e.target.closest && e.target.closest('[data-magnetic]');
      if (btn && !btn.contains(e.relatedTarget)) btn.style.transform = '';
    });
  }

  // ===== CUSTOM CURSOR =====
  // Selector tracks the revibe-* class names actually emitted by the sections.
  var CURSOR_HOVER_SELECTOR = [
    'a', 'button',
    '.revibe-h-card', '.revibe-h2-card', '.revibe-g-item', '.revibe-tl-card',
    '.revibe-t-card', '.revibe-e-item', '.revibe-i-item', '.revibe-f-q',
    '.revibe-bk-submit', '.revibe-bk-select'
  ].join(',');

  function initCursor() {
    var cr = document.getElementById('cr');
    var cd = document.getElementById('cd');
    if (!cr || !cd) return;
    // Touch devices hide the cursor in CSS; skip the rAF loop entirely there.
    if (COARSE) return;

    var mx = 0, my = 0, cx = 0, cy = 0;
    document.addEventListener('mousemove', function (e) { mx = e.clientX; my = e.clientY; }, { passive: true });

    function updateCursor() {
      cx += (mx - cx) * 0.15;
      cy += (my - cy) * 0.15;
      cr.style.left = cx + 'px';
      cr.style.top = cy + 'px';
      cd.style.left = mx + 'px';
      cd.style.top = my + 'px';
      requestAnimationFrame(updateCursor);
    }
    requestAnimationFrame(updateCursor);

    // Delegated so elements added later (theme editor, lazy sections) still work.
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest && e.target.closest(CURSOR_HOVER_SELECTOR)) cr.classList.add('hover');
    });
    document.addEventListener('mouseout', function (e) {
      if (e.target.closest && e.target.closest(CURSOR_HOVER_SELECTOR)) cr.classList.remove('hover');
    });
  }

  // ===== MOUSE LIGHT =====
  // Optional ambient glow; only runs if the theme renders #ml.
  function initMouseLight() {
    var ml = document.getElementById('ml');
    if (!ml || COARSE || REDUCED) return;
    document.addEventListener('mousemove', function (e) {
      ml.style.left = e.clientX + 'px';
      ml.style.top = e.clientY + 'px';
    }, { passive: true });
  }

  // ===== HERO PARTICLES =====
  // Any section can opt in with [data-particles]; count via data-particle-count.
  function initParticles(scope) {
    if (REDUCED) return;
    (scope || document).querySelectorAll('[data-particles]').forEach(function (host) {
      if (host.dataset.particlesReady === '1') return;
      host.dataset.particlesReady = '1';
      var count = parseInt(host.getAttribute('data-particle-count'), 10);
      if (isNaN(count) || count < 1) count = 30;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < count; i++) {
        var p = document.createElement('div');
        p.className = 'revibe-hero-particle';
        p.style.left = Math.random() * 100 + '%';
        p.style.top = Math.random() * 100 + '%';
        p.style.animationDelay = Math.random() * 10 + 's';
        p.style.animationDuration = (8 + Math.random() * 6) + 's';
        frag.appendChild(p);
      }
      host.appendChild(frag);
    });
  }

  // ===== FLOATING ORB PARALLAX =====
  // Each [data-orb] may set its own speed via data-orb-speed (default .03).
  function initOrbParallax() {
    if (REDUCED) return;
    var orbs = document.querySelectorAll('[data-orb]');
    if (!orbs.length) return;
    var ticking = false;
    function apply() {
      var st = window.scrollY;
      orbs.forEach(function (orb) {
        var speed = parseFloat(orb.getAttribute('data-orb-speed'));
        if (isNaN(speed)) speed = 0.03;
        orb.style.transform = 'translateY(' + st * speed + 'px)';
      });
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }, { passive: true });
    apply();
  }

  function init() {
    initReveals(document);
    initParticles(document);
    initCursor();
    initMouseLight();
    initSmoothScroll();
    initMagneticButtons();
    initOrbParallax();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-wire reveals/particles for a section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    initReveals(e.target);
    initParticles(e.target);
  });
})();
